import React, {useCallback, useEffect, useRef, useState} from 'react';
import {SafeAreaView, Text} from 'react-native';
import {useSearchParams} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import {XMarkIcon, PlusIcon} from '@heroicons/react/24/outline';
import ListingsHeaderV2 from '../../MainListing/ListingsHeaderV2';
import Modal from '../UI/modals/Modal';
import {useThemeColors} from '../../HelperClasses/ThemeContext';
import typography from '../../StyleSheets/TypographyTokens';
import {adminApi} from './adminApi';
import LocationPicker from '../../AddListing/components/LocationPicker';
import {useLocale} from '../../Localization/LocaleContext';

/* ── Data ───────────────────────────────────────────────────── */

export const useApiList = path => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!path) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setItems(await adminApi.list(path));
    } catch (e) {
      setItems([]);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {items, loading, error, reload};
};

/* ── Formatting ─────────────────────────────────────────────── */

export const humanize = value =>
  value ? String(value).replace(/[_-]+/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '—';

export const formatDate = (value, locale) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(locale === 'fr' ? 'fr-FR' : locale || undefined, {day: 'numeric', month: 'short', year: 'numeric'});
};

export const formatDateTime = (value, locale) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(locale === 'fr' ? 'fr-FR' : locale || undefined, {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});
};

export const formatMoney = (amount, currency, locale) => {
  if (amount === undefined || amount === null || amount === '') return '—';
  try {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : locale || undefined, {style: 'currency', currency: currency || 'USD'}).format(Number(amount));
  } catch (_) {
    return `${amount} ${currency || ''}`.trim();
  }
};

export const staffName = staff => {
  if (!staff) return '—';
  const user = staff.user || {};
  const name =
    staff.name ||
    staff.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.name ||
    user.email;
  return name || (staff.user_id ? `User #${staff.user_id}` : `Staff #${staff.id}`);
};

export const staffOption = staff => ({
  value: staff.id,
  label: staff.employee_code ? `${staffName(staff)} · ${staff.employee_code}` : staffName(staff),
});

export const lookup = (items, id, render) => {
  const match = items.find(item => String(item.id) === String(id));
  return match ? render(match) : id ? `#${id}` : '—';
};

export const currentMonthRange = () => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const last = new Date(y, m, 0).getDate();
  return {from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}`};
};

/* ── Page shell ─────────────────────────────────────────────── */

export const PrimaryButton = ({children, onClick, disabled, icon = true}) => (
  <button
    type="submit"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
    style={{background: 'var(--color-accent)', color: '#fff'}}>
    {icon && <PlusIcon className="w-4 h-4" />}
    {children}
  </button>
);

export const SecondaryButton = ({children, onClick, disabled}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-lg border px-3.5 py-2 text-sm font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
    style={{borderColor: 'var(--color-border)', color: 'var(--color-text)', background: 'var(--color-surface)'}}>
    {children}
  </button>
);

export const useTabParam = (tabs, fallback) => {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const active = tabs.some(t => t.key === requested) ? requested : fallback;
  const setActive = key => setParams({tab: key}, {replace: true});
  return [active, setActive];
};

export const AppPage = ({title, tabs, activeTab, onTabChange, action, children}) => {
  const themeColors = useThemeColors();
  const {translate: t} = useLocale();
  return (
    <SafeAreaView style={{flex: 1, backgroundColor: themeColors.screenBackground}}>
      <ToastContainer />
      <ListingsHeaderV2
        simple
        titleArea={<Text style={{...typography.display, color: themeColors.primaryText}}>{t(title)}</Text>}
        actionControl={action}
      />
      {tabs?.length > 0 && (
      <div
        className="flex gap-1 px-6 overflow-x-auto shrink-0"
        style={{borderBottom: '1px solid var(--color-border)'}}>
        {tabs.map(tab => {
          const active = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className="px-3 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors"
              style={{
                color: active ? 'var(--color-text)' : 'var(--color-text-secondary)',
                borderBottom: `2px solid ${active ? 'var(--color-accent)' : 'transparent'}`,
                marginBottom: -1,
              }}>
              {t(tab.label)}
            </button>
          );
        })}
      </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 pb-12">{children}</div>
    </SafeAreaView>
  );
};

export const Toolbar = ({children}) => (
  <div className="flex flex-wrap items-end gap-3 mb-4">{children}</div>
);

/* ── Status ─────────────────────────────────────────────────── */

const TONES = {
  success: ['approved', 'reimbursed', 'paid', 'active', 'completed', 'present', 'received'],
  danger: ['rejected', 'cancelled', 'failed', 'absent'],
  accent: ['submitted', 'processing', 'clocked_in', 'sent', 'partial'],
};

export const StatusBadge = ({status}) => {
  const {translate: t} = useLocale();
  const value = String(status || 'draft').toLowerCase();
  const color = TONES.success.includes(value)
    ? 'var(--toast-success)'
    : TONES.danger.includes(value)
      ? 'var(--toast-error)'
      : TONES.accent.includes(value)
        ? 'var(--color-accent)'
        : 'var(--color-text-secondary)';
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{color, background: `color-mix(in srgb, ${color} 12%, transparent)`}}>
      {t(`common.status.${value}`, humanize(value))}
    </span>
  );
};

export const RowAction = ({children, onClick, disabled, tone = 'default'}) => (
  <button
    type="button"
    onClick={e => {
      e.stopPropagation();
      onClick();
    }}
    disabled={disabled}
    className="rounded-md border px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
    style={{
      borderColor: tone === 'primary' ? 'var(--color-accent)' : 'var(--color-border)',
      color: tone === 'primary' ? 'var(--color-accent)' : tone === 'danger' ? 'var(--toast-error)' : 'var(--color-text)',
      background: 'var(--color-surface)',
    }}>
    {children}
  </button>
);

/* ── Table ──────────────────────────────────────────────────── */

export const DataTable = ({columns, rows, loading, error, emptyText, onRowClick, rowKey = row => row.id}) => {
  const {translate: t} = useLocale();
  const message = loading ? t('common.loading') : error ? error : !rows.length ? t(emptyText) : null;
  return (
    <div
      className="rounded-xl border overflow-x-auto"
      style={{borderColor: 'var(--color-border)', background: 'var(--color-surface)'}}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{background: 'var(--color-table-header)'}}>
            {columns.map(col => (
              <th
                key={col.key}
                className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                style={{color: 'var(--color-text-secondary)'}}>
                {t(col.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {message ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center"
                style={{color: error && !loading ? 'var(--toast-error)' : 'var(--color-text-secondary)'}}>
                {message}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row) ?? index}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'cursor-pointer hover:opacity-80' : ''}
                style={{borderTop: '1px solid var(--color-border)'}}>
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 align-middle ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    style={{color: col.muted ? 'var(--color-text-secondary)' : 'var(--color-text)'}}>
                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ── Form fields ────────────────────────────────────────────── */

const inputStyle = {
  background: 'var(--color-input)',
  borderColor: 'var(--color-border)',
  color: 'var(--color-text)',
};

export const FieldLabel = ({children, required}) => (
  <p className="text-xs font-semibold mb-1" style={{color: 'var(--color-text-secondary)'}}>
    {children}
    {required && <span style={{color: 'var(--toast-error)'}}> *</span>}
  </p>
);

export const SelectInput = ({value, onChange, options, placeholder = 'common.select', className = ''}) => {
  const {translate: t} = useLocale();
  return <select
    className={`form-input py-2 w-full ${className}`}
    style={inputStyle}
    value={value ?? ''}
    onChange={e => onChange(e.target.value)}>
    <option value="">{t(placeholder)}</option>
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>
        {t(opt.label)}
      </option>
    ))}
  </select>;
};

export const DateField = ({label, value, onChange}) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input
      type="date"
      className="form-input py-2"
      style={inputStyle}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

const userLabel = user =>
  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.name || user.email || `User #${user.id}`;

export const searchUsers = async query => {
  const users = await adminApi.list(
    `v1/users?search_key=${encodeURIComponent(query)}&page=1&per_page=8`,
  );
  return users.map(user => ({
    value: user.id,
    label: userLabel(user),
    sub: user.email && user.email !== userLabel(user) ? user.email : '',
  }));
};

export const searchAccounts = async query => {
  const accounts = await adminApi.list(
    `v1/accounts/?page=1&per_page=8&type=accounts&search_key=${encodeURIComponent(query)}`,
  );
  return accounts.map(account => ({
    value: account.id,
    label: account.name || `Account #${account.id}`,
    sub: account.user?.email || '',
  }));
};

const SearchPicker = ({value, onChange, search, placeholder, initialLabel}) => {
  const {translate: t} = useLocale();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState(value && initialLabel ? {value, label: initialLabel} : null);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      setHasSearched(false);
      try {
        setResults(await search(query.trim()));
        setHasSearched(true);
      } catch (_) {
        setResults([]);
        setHasSearched(true);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query, search]);

  if (value && selected) {
    return (
      <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm" style={inputStyle}>
        <span>
          {selected.label}
          {selected.sub && <span style={{color: 'var(--color-text-secondary)'}}> · {selected.sub}</span>}
        </span>
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            onChange('');
          }}>
          <XMarkIcon className="w-4 h-4" style={{color: 'var(--color-text-secondary)'}} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="form-input py-2 w-full"
        style={inputStyle}
        placeholder={t(placeholder)}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {query.trim().length >= 2 && (searching || hasSearched) && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border shadow-lg max-h-56 overflow-y-auto"
          style={{background: 'var(--color-surface)', borderColor: 'var(--color-border)'}}>
          {searching ? (
            <div className="px-3 py-3 text-sm" style={{color: 'var(--color-text-secondary)'}}>{t('common.searching')}</div>
          ) : results.length > 0 ? results.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSelected(option);
                  setResults([]);
                  setQuery('');
                  setHasSearched(false);
                  onChange(option.value);
                }}
                className="block w-full text-left px-3 py-2 text-sm hover:opacity-70"
                style={{color: 'var(--color-text)'}}>
                {option.label}
                {option.sub && <span style={{color: 'var(--color-text-secondary)'}}> · {option.sub}</span>}
              </button>
            )) : (
              <div className="px-3 py-3 text-sm" style={{color: 'var(--color-text-secondary)'}}>{t('common.no_matches')}</div>
            )}
        </div>
      )}
    </div>
  );
};

const FieldInput = ({field, value, onChange}) => {
  const {translate: t} = useLocale();
  const applySuggestion = suggestion => {
    const selected = String(suggestion);
    if (field.suggestionMode === 'append' && value) {
      onChange(`${value}, ${selected}`);
    } else {
      onChange(selected);
    }
  };
  const suggestions = field.suggestions?.length > 0 && (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {field.suggestions.map(suggestion => {
        const label = typeof suggestion === 'object' ? suggestion.label : suggestion;
        const valueToUse = typeof suggestion === 'object' ? suggestion.value : suggestion;
        return (
          <button
            key={String(label)}
            type="button"
            onClick={() => applySuggestion(valueToUse)}
            className="rounded-full border px-2.5 py-1 text-xs transition-opacity hover:opacity-80"
            style={{borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-surface)'}}>
            {label}
          </button>
        );
      })}
    </div>
  );
  if (field.type === 'select') {
    return <SelectInput value={value} onChange={onChange} options={field.options || []} placeholder={field.placeholder || 'common.select'} />;
  }
  if (field.type === 'user') {
    return <SearchPicker value={value} onChange={onChange} search={searchUsers} placeholder={t('common.search_users')} />;
  }
  if (field.type === 'search') {
    return (
      <SearchPicker
        value={value}
        onChange={onChange}
        search={field.search}
        placeholder={t(field.placeholder)}
        initialLabel={field.initialLabel}
      />
    );
  }
  if (field.type === 'location') {
    return (
      <LocationPicker
        location={value || ''}
        onLocationChange={(_, address) => onChange(address || '')}
        hideLabel
      />
    );
  }
  if (field.type === 'textarea') {
    return (
      <>
        <textarea
          className="form-input py-2 w-full resize-none"
          style={inputStyle}
          rows={3}
          placeholder={t(field.placeholder)}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
        />
        {suggestions}
      </>
    );
  }
  const handleInputChange = e => {
    let nextValue = e.target.value;
    if (field.type === 'tel') {
      // Phone numbers may include international prefixes and common
      // separators, but alphabetic input should never reach the payload.
      nextValue = nextValue.replace(/[^0-9+().\s-]/g, '');
    }
    if (field.name === 'currency' || field.name?.endsWith('_currency')) {
      nextValue = nextValue.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase();
    }
    onChange(nextValue);
  };
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm" style={{color: 'var(--color-text)'}}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          className="w-4 h-4 accent-[var(--color-accent)]"
        />
        {t(field.checkboxLabel)}
      </label>
    );
  }
  return (
    <>
      <input
        type={field.type || 'text'}
        className="form-input py-2 w-full"
        style={inputStyle}
        placeholder={t(field.placeholder)}
        value={value ?? ''}
        min={field.min}
        step={field.step}
        inputMode={field.inputMode}
        pattern={field.pattern}
        onChange={handleInputChange}
      />
      {suggestions}
    </>
  );
};

/* ── Form modal ─────────────────────────────────────────────── */

const toPayload = (fields, values, sendEmpty) => {
  const payload = {};
  fields.forEach(field => {
    const value = values[field.name];
    if (value === '' || value === undefined || value === null) {
      if (sendEmpty) payload[field.name] = field.numeric || field.type === 'number' ? null : '';
      return;
    }
    payload[field.name] =
      field.type === 'number' || field.numeric ? Number(value) : value;
  });
  return payload;
};

const fieldValidationMessage = (field, value) => {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  const name = String(field.name || '').toLowerCase();
  const text = String(value).trim();
  if (field.type === 'email' || name === 'email' || name.endsWith('_email')) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) ? '' : 'common.validation.email';
  }
  if (field.type === 'tel' || /(^|_)(phone|mobile|telephone)(_|$)/.test(name)) {
    return /^\+?[0-9() .-]{7,}$/.test(text) && (text.match(/\d/g) || []).length >= 7
      ? ''
      : 'common.validation.phone';
  }
  if (name === 'currency' || name.endsWith('_currency')) {
    return /^[A-Za-z]{3}$/.test(text) ? '' : 'common.validation.currency';
  }
  if (field.type === 'location' || name === 'address' || name.endsWith('_address')) {
    return text.length >= 3 ? '' : 'common.validation.location';
  }
  return '';
};

const formInitialValues = (fields, initialValues) => {
  const initial = {};
  fields.forEach(field => {
    initial[field.name] = initialValues[field.name] ?? field.defaultValue ?? (field.type === 'checkbox' ? false : '');
  });
  return initial;
};

const readFormDraft = (draftKey, initial) => {
  if (!draftKey || typeof window === 'undefined') return initial;
  try {
    const stored = window.localStorage.getItem(draftKey);
    return stored ? {...initial, ...JSON.parse(stored)} : initial;
  } catch (_) {
    return initial;
  }
};

export const FormModal = ({title, description, fields, initialValues = {}, submitLabel = 'Save', sendEmpty = false, draftKey, resetLabel = 'Reset', onSubmit, onClose}) => {
  const {translate: t} = useLocale();
  const initial = formInitialValues(fields, initialValues);
  const [values, setValues] = useState(() => {
    return readFormDraft(draftKey, initial);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!draftKey || typeof window === 'undefined') return undefined;
    try {
      window.localStorage.setItem(draftKey, JSON.stringify(values));
    } catch (_) {}
    return undefined;
  }, [draftKey, values]);

  const clearDraft = () => {
    if (!draftKey || typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(draftKey);
    } catch (_) {}
  };

  const discard = () => {
    clearDraft();
    onClose();
  };

  const reset = () => {
    clearDraft();
    setValues(initial);
    setError('');
  };

  const submit = async e => {
    e.preventDefault();
    const missing = fields.find(
      field => field.required && (values[field.name] === '' || values[field.name] === undefined || String(values[field.name]).trim() === ''),
    );
    if (missing) {
      setError(t('common.validation.required', {label: t(missing.label)}));
      return;
    }
    const invalid = fields.find(field => {
      const sharedMessage = fieldValidationMessage(field, values[field.name]);
      if (sharedMessage) return true;
      if (!field.pattern || !values[field.name]) return false;
      return !(new RegExp(field.pattern).test(String(values[field.name])));
    });
    if (invalid) {
      setError(invalid.validationMessage ? t(invalid.validationMessage) : fieldValidationMessage(invalid, values[invalid.name]) ? t(fieldValidationMessage(invalid, values[invalid.name])) : t('common.validation.invalid', {label: t(invalid.label)}));
      return;
    }
    setSaving(true);
      setError('');
    try {
      await onSubmit(toPayload(fields, values, sendEmpty));
      clearDraft();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal onCloseModal={onClose}>
      <form
        onSubmit={submit}
        className="w-[calc(100vw-32px)] md:w-[520px] max-h-[88vh] rounded-xl shadow-xl flex flex-col"
        style={{background: 'var(--color-surface)'}}>
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div>
            <h3 className="text-base font-semibold" style={{color: 'var(--color-text)'}}>{t(title)}</h3>
            {description && (
              <p className="text-xs mt-0.5" style={{color: 'var(--color-text-secondary)'}}>{t(description)}</p>
            )}
          </div>
          <button type="button" onClick={discard} className="opacity-60 hover:opacity-100 transition-opacity">
            <XMarkIcon className="w-5 h-5" style={{color: 'var(--color-text)'}} />
          </button>
        </div>
        <div className="px-6 pb-2 overflow-y-auto grid grid-cols-2 gap-x-3 gap-y-4">
          {fields.map(field => (
            <div key={field.name} className={field.half ? 'col-span-2 md:col-span-1' : 'col-span-2'}>
              {field.type !== 'checkbox' && <FieldLabel required={field.required}>{t(field.label)}</FieldLabel>}
              <FieldInput
                field={field}
                value={values[field.name]}
                onChange={value => setValues(current => ({...current, [field.name]: value}))}
              />
            </div>
          ))}
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2">
          {error && (
            <p className="text-xs mr-auto" style={{color: 'var(--toast-error)'}}>{error}</p>
          )}
          <SecondaryButton onClick={reset}>{t(resetLabel)}</SecondaryButton>
          <SecondaryButton onClick={discard}>{t('common.cancel')}</SecondaryButton>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{background: 'var(--color-accent)', color: '#fff'}}>
            {saving ? t('common.saving') : t(submitLabel)}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ── Detail modal ───────────────────────────────────────────── */

export const DetailModal = ({title, rows, loading, error, onClose}) => {
  const {translate: t} = useLocale();
  return <Modal onCloseModal={onClose}>
    <div
      className="w-[calc(100vw-32px)] md:w-[520px] max-h-[88vh] rounded-xl shadow-xl flex flex-col"
      style={{background: 'var(--color-surface)'}}>
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h3 className="text-base font-semibold" style={{color: 'var(--color-text)'}}>{t(title)}</h3>
        <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100 transition-opacity">
          <XMarkIcon className="w-5 h-5" style={{color: 'var(--color-text)'}} />
        </button>
      </div>
      <div className="px-6 pb-6 overflow-y-auto">
        {loading && <p className="text-sm py-6" style={{color: 'var(--color-text-secondary)'}}>{t('common.loading')}</p>}
        {error && <p className="text-sm py-6" style={{color: 'var(--toast-error)'}}>{error}</p>}
        {!loading && !error && (
          <dl className="grid grid-cols-[140px_1fr] gap-y-3 text-sm">
            {rows.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt style={{color: 'var(--color-text-secondary)'}}>{t(label)}</dt>
                <dd style={{color: 'var(--color-text)'}}>{value ?? '—'}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </div>
    </div>
  </Modal>;
};
